import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Result } from '@de/types/result';
import type {
  ResultsPageExitStrategyExtraCtaConfig,
  StudentLoansInlineDslContent,
} from '@de/types/types';
import type { ResolvedDataSource } from '@de/providers/DataSourceManager.types';
import {
  mapOfferingDetailToResultDetailFields,
  offeringDetailMatchesResult,
  resolveOfferingDetailOrg,
} from '@de/utils/mappers/mapOfferingDetailToResultDetailFields';
import type Lead from '@de/types/leadDelivery';
import type { LeadSubmitResponse } from '@highereducation/delivery-system';
import { SubmissionStatus } from '@de/components/ResultsPage/types';
import { mergePartnerIdentifiers } from '@de/utils/mergePartnerIdentifiers';
import { useSubmittedProgramsOptional } from '@de/providers/SubmittedProgramsProvider';
import { LeadFlowDrawerBoundary } from '@de/components/LeadFlowDrawerBoundary';
import DuplicateSubmissionPopup from '@de/components/DuplicateSubmissionPopup';
import { SingleSubmissionStep } from '@de/components/SingleProgramSubmission/types';
import SingleProgramSubmission from '@de/components/SingleProgramSubmission/Default';
import SingleSubmissionDrawer from '@de/components/SingleProgramSubmission/SingleSubmissionDrawer';
import useSingleSubmissionSlideout from '@de/hooks/useSingleSubmissionSlideout';
import { useLeadSubmissionWithQuestions } from '@de/hooks/useLeadSubmissionWithQuestions';
import useActiveProspectScript from '@de/hooks/useActiveProspectScript';
import useJornayaScript from '@de/hooks/useJornayaScript';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import useDuplicatePrograms from '@de/hooks/useDuplicatePrograms';
import {
  getCurrentFieldValues,
  transformFlowStateToLeadInputs,
} from '@de/utils/dataFromFlowUtils';
import buildLeadObject from '@de/utils/buildLeadObject';
import { newRelicNoticeError } from '@de/utils/newRelicActions';
import { getSubmissionErrorStatus } from '@de/utils/getSubmissionErrorStatus';
import { isSubmittableResult } from '@de/utils/configurable-step/isSubmittableResult';
import { ensureConversion } from '@de/utils/ensureAppViewConversion';
import { useSiteConfig } from '@de/providers/ExperienceConfigProvider';
import { useResultsPageConfig } from '@de/providers/ResultsPageConfigProvider';
import { uuid } from '@de/shared/utils';

/**
 * Where the governed lead-capture surface renders:
 * - `'drawer'` (default): the platform-owned `SingleSubmissionDrawer`
 *   slides over the page. Used by regular DSL result-card CTAs.
 * - `'inline'`: the provider renders no chrome; the caller mounts
 *   {@link LeadFlowContextValue.leadFlowContent} itself (e.g. inside the
 *   Recommender / Spotlight popup modal) so the user never leaves the
 *   popup view.
 */
export type LeadFlowRenderMode = 'drawer' | 'inline';

/**
 * How long the governed drawer holds on the full-screen "submitted"
 * status panel after a successful lead submission before advancing to
 * the exit-strategy view ("What to expect from the advisor?"). Both
 * submit paths (regular + additional-questions) share this so the hold
 * can't drift between them. Mirrors the imperative ResultsPage submit
 * path.
 */
const SUBMITTED_PANEL_HOLD_MS = 1500;

/** Options accepted by {@link LeadFlowContextValue.openLeadFlow}. */
export interface OpenLeadFlowOptions {
  /** Render target for the governed surface. Defaults to `'drawer'`. */
  readonly mode?: LeadFlowRenderMode;
  /**
   * Skip same-school duplicate branching and open the selected result
   * directly. Defaults to false. The governed "view next steps" reopen
   * path drives `openGovernedFlow` directly, so this flag only matters
   * for external callers that want to bypass the duplicate popup.
   */
  readonly skipDuplicateCheck?: boolean;
}

export type SubmittedResultSummary = {
  readonly programId?: number;
  readonly schoolId?: string;
};

/**
 * Value exposed by {@link useLeadFlow}. The governed lead-capture entry
 * point a DSL result-card CTA reaches through the `results.openLeadFlow`
 * action.
 */
export interface LeadFlowContextValue {
  /**
   * Hand off the selected result and open the governed lead-capture
   * surface for it. Called by the `results.openLeadFlow` action handler
   * wired in `ConfigurableStep`; the DSL passes the repeater item
   * verbatim (`params.result: '{{item}}'`).
   *
   * Pass `{ mode: 'inline' }` to keep the governed surface out of the
   * drawer so the caller can render {@link leadFlowContent} in place
   * (e.g. inside the popup modal); omit it for the default drawer.
   */
  readonly openLeadFlow: (
    result: Result,
    options?: OpenLeadFlowOptions,
  ) => void;
  /** Close the governed lead-capture surface (drawer or inline). */
  readonly closeLeadFlow: () => void;
  /** The result currently handed off to the governed flow, if any. */
  readonly selectedResult?: Result;
  /** Whether the governed surface is currently open. */
  readonly isOpen: boolean;
  /** The active render target for the governed surface. */
  readonly mode: LeadFlowRenderMode;
  /** First name derived from flow answers and any in-flow PII edits. */
  readonly firstName?: string;
  /**
   * The governed submission surface (`SingleProgramSubmission`) for the
   * selected result, or `null` when no result is handed off. Inline
   * callers render this node themselves; in `'drawer'` mode the provider
   * renders it inside its own `SingleSubmissionDrawer`.
   */
  readonly leadFlowContent: React.ReactNode;
  /** Submitted result summary consumed by comparison/recommendation UI. */
  readonly submittedResults: SubmittedResultSummary[];
  /**
   * Receive the resolved `offeringDetail` DSM source so the provider can
   * merge the by-id detail projection
   * (`GET /v1/offerings/:id` → `data.organizations[0]`) onto the open
   * result. Called by the `<OfferingDetailObserver>` mounted inside the
   * DataSourceManager — `LeadFlowProvider` is an **ancestor** of the DSM
   * (it wraps the ConfigurableStep bridge), so it cannot read the DSM via
   * `useOptionalDataSourceManager()` directly; the observer lifts the
   * source up to here. Optional: non-DSL hosts / tests may omit it.
   */
  readonly applyOfferingDetail?: (
    source: ResolvedDataSource | undefined,
  ) => void;
}

/**
 * Provider-less default is `undefined` so `useLeadFlowOptional()` can
 * detect host ownership (mirrors `ItemScopeContext` /
 * `useComparisonOptional`). `useLeadFlow()` falls back to a safe no-op
 * so a DSL component dispatching the action outside a provider degrades
 * to a single warn instead of throwing.
 */
const LeadFlowContext = createContext<LeadFlowContextValue | undefined>(
  undefined,
);

let openLeadFlowOutsideProviderWarned = false;

const EMPTY_SUBMITTED_RESULTS: SubmittedResultSummary[] = [];

const NOOP_LEAD_FLOW: LeadFlowContextValue = Object.freeze({
  openLeadFlow: () => {
    if (!openLeadFlowOutsideProviderWarned) {
      openLeadFlowOutsideProviderWarned = true;
      // eslint-disable-next-line no-console
      console.warn(
        '[LeadFlowProvider] openLeadFlow dispatched without a LeadFlowProvider in scope; dropping.',
      );
    }
  },
  closeLeadFlow: () => {},
  selectedResult: undefined,
  isOpen: false,
  mode: 'drawer',
  firstName: undefined,
  leadFlowContent: null,
  submittedResults: EMPTY_SUBMITTED_RESULTS,
  applyOfferingDetail: () => {},
});

export interface LeadFlowProviderProps {
  readonly children: React.ReactNode;
  /**
   * DSL result-step config id (`dslConfig.configId`). Forwarded to
   * `useLeadSubmissionWithQuestions` so the governed lead-submit
   * `FormSubmitted` carries `dslConfigId` + result-step context on the
   * wire. Optional: the provider also serves non-DSL surfaces / tests
   * that mount it without a config id.
   */
  readonly dslConfigId?: string;
  readonly flowType?: string;
  readonly trafficSource?: string;
  readonly monetizationChannel?: string;
  readonly studentLoansInlineContent?: StudentLoansInlineDslContent;
  readonly extraCtaConfig?: ResultsPageExitStrategyExtraCtaConfig;
}

const EMPTY_RESULT_IDS: string[] = [];

/**
 * Governed lead-capture seam for the DSL render path.
 *
 * The DSL render path (`ConfigurableStep` → `DslConfig` →
 * `platform.dataRepeater` → result cards) is intentionally separate
 * from the imperative `ResultsPage`, which owns the conversion drawer
 * inline. This provider makes the **existing** governed lead-capture
 * runtime reachable from the DSL path WITHOUT a second conversion state
 * machine: it reuses the same governed components and hooks the
 * imperative page uses — `useSingleSubmissionSlideout` (drawer + step
 * state), `useLeadSubmissionWithQuestions` (PRQ + lead submission), and
 * the `SingleProgramSubmission` drawer surface — and exposes a single
 * `openLeadFlow(result)` entry point.
 *
 * The DSL only expresses intent (`results.openLeadFlow` with the
 * selected `item`); every compliance and submission concern stays here
 * in platform code. The imperative `ResultsPage` is untouched.
 */
export const LeadFlowProvider: React.FC<LeadFlowProviderProps> = ({
  children,
  dslConfigId,
  flowType,
  trafficSource,
  monetizationChannel,
  studentLoansInlineContent,
  extraCtaConfig,
}) => {
  const [selectedResult, setSelectedResult] = useState<Result | undefined>();
  // Latest `offeringDetail` DSM source, lifted up from the observer mounted
  // inside the DataSourceManager. Drives the non-destructive drawer-detail
  // merge below. `undefined` until the observer first reports (non-DSL hosts
  // never set it).
  const [offeringDetailSource, setOfferingDetailSource] = useState<
    ResolvedDataSource | undefined
  >(undefined);
  // Render target for the governed surface. Regular DSL result cards use
  // the default drawer; the Recommender / Spotlight popup opens it
  // `'inline'` so the user stays inside the popup modal.
  const [renderMode, setRenderMode] = useState<LeadFlowRenderMode>('drawer');
  // The result whose submission was intercepted by the governed
  // duplicate-school rule. When set, the
  // governed `DuplicateSubmissionPopup` renders instead of opening the
  // lead flow. `undefined` = no duplicate interception active.
  const [duplicateResult, setDuplicateResult] = useState<Result | undefined>();
  // Options from the intercepted attempt, replayed by "view next steps"
  // so the reopened flow keeps its original render target (e.g. inline
  // inside the popup rather than a competing drawer).
  const [duplicateOptions, setDuplicateOptions] = useState<
    OpenLeadFlowOptions | undefined
  >();

  const { flowState, steps } = useDynamicExperienceContext();

  // Submitted-programs registry. Optional so the provider
  // still works standalone (e.g. tests / non-result surfaces). Read
  // lazily through a ref so recording a submission — which changes the
  // registry's context identity — doesn't rebuild the governed callbacks.
  const submitted = useSubmittedProgramsOptional();
  const submittedRef = useRef(submitted);
  submittedRef.current = submitted;

  // PII derived from prior-step flow answers, editable inside the
  // governed drawer — same source the imperative ResultsPage uses
  // (`transformFlowStateToLeadInputs`).
  const flowInputs = useMemo(
    () => transformFlowStateToLeadInputs(flowState ?? {}),
    [flowState],
  );
  const flowAnswers = useMemo(
    () => getCurrentFieldValues(steps ?? [], flowState ?? {}),
    [steps, flowState],
  );

  const [piiFirstName, setPiiFirstName] = useState<string>('');
  const [piiLastName, setPiiLastName] = useState<string>('');
  const [piiEmail, setPiiEmail] = useState<string>('');
  const [piiPhone, setPiiPhone] = useState<string>('');
  const [editMode, setEditMode] = useState<boolean>(false);
  const [leadSubmissionStatus, setLeadSubmissionStatus] = useState<
    SubmissionStatus | undefined
  >(undefined);

  // Conversion created at the application-view-routing click (governed flow
  // open, PII entry). Threaded into the submit hook so submit completes this
  // same conversion instead of minting a duplicate (Supersonic `APP_VIEWED`
  // parity: create at view/click time, complete at submit). Reset on every new
  // governed open so a stale id can't leak across selections.
  const [clickConversionId, setClickConversionId] = useState<
    string | undefined
  >(undefined);
  // Generic partner identifiers (`referenceId` + `partnerIdentifiers`) read
  // off the LDS v4 submit response (EDUN-14483). Threaded into
  // `SingleProgramSubmission` (and from there, the exit-strategy CTA
  // sources) so the governed Apply / extra CTA interpolators can resolve
  // fieldMap entries like `partnerIdentifiers.studentId`. Reset only on a
  // fresh PII-entry open (see `openGovernedFlow`) so a stale identifier from
  // a prior program can't leak into a new selection's CTA, while a reopen of
  // an already-submitted program (exit-strategy entry) keeps its own.
  const [partnerIdentifiers, setPartnerIdentifiers] = useState<
    Record<string, string> | undefined
  >(undefined);
  // Latest selected result, read by the submit callbacks so a late
  // response is not applied to a newer program.
  const selectedResultRef = useRef(selectedResult);
  selectedResultRef.current = selectedResult;

  const handleLeadSubmitted = useCallback(
    (response: LeadSubmitResponse, submittedResult?: Result) => {
      if (submittedResult && selectedResultRef.current !== submittedResult) {
        return;
      }
      setPartnerIdentifiers(mergePartnerIdentifiers(response));
    },
    [],
  );
  // Monotonic token bumped on every governed open. The click-time conversion
  // create is async; two rapid opens could otherwise resolve out of order and
  // land one selection's conversionId in the other's state. The create's
  // `.then` only applies its result while its token is still current, so a
  // superseded open's late resolve is ignored.
  const conversionRequestRef = useRef(0);
  // The open selection's `ensureConversion` promise, assigned synchronously in
  // `openGovernedFlow` before that create can settle. Submit awaits it so a
  // click that has not stored an id yet still completes the same conversion.
  const clickConversionPromiseRef = useRef<
    Promise<string | undefined> | undefined
  >(undefined);
  // Remembers the click-time conversion already minted for a selection (keyed by
  // the card's stable `productClickedCorrelationId`) so reopening the same card
  // pre-submit reuses it instead of minting a second created-but-uncompleted
  // conversion — the same once-per-selection posture the `window.open` surfaces
  // get through `useAppViewConversion`.
  const createdConversionRef = useRef<Map<string, string>>(new Map());
  // The same selection's create, while it has not stored an id yet. A second
  // open of that card reuses this promise instead of calling `ensureConversion`
  // again. Removed when the create settles, whether it stored an id or failed.
  const inflightConversionRef = useRef<
    Map<string, Promise<string | undefined>>
  >(new Map());
  const siteConfig = useSiteConfig();

  // Seed PII from flow answers whenever they resolve (location
  // inference / autopopulation lands asynchronously in the provider).
  useEffect(() => {
    setPiiFirstName(
      (prev) => prev || (flowInputs.firstName?.value?.toString() ?? ''),
    );
    setPiiLastName(
      (prev) => prev || (flowInputs.lastName?.value?.toString() ?? ''),
    );
    setPiiEmail((prev) => prev || (flowInputs.email?.value?.toString() ?? ''));
    setPiiPhone(
      (prev) => prev || (flowInputs.phoneNumber?.value?.toString() ?? ''),
    );
  }, [flowInputs]);

  const inputs = useMemo(
    () => ({
      ...flowInputs,
      ...(!flowInputs.country
        ? { country: { key: 'country' as const, value: 'US' } }
        : {}),
      firstName: { key: 'firstName' as const, value: piiFirstName },
      lastName: { key: 'lastName' as const, value: piiLastName },
      email: { key: 'email' as const, value: piiEmail },
      phoneNumber: { key: 'phoneNumber' as const, value: piiPhone },
    }),
    [flowInputs, piiFirstName, piiLastName, piiEmail, piiPhone],
  );

  const lead = useMemo(() => buildLeadObject(inputs) as Lead, [inputs]);
  const leadRef = useRef(lead);

  useEffect(() => {
    leadRef.current = lead;
  }, [lead]);

  // Submitted programs surfaced read-only to comparison / recommendation
  // UI. Sourced from the registry so the lead flow and the DSL render
  // path agree on a single canonical submitted list. Copied into a fresh
  // array (stable via the registry's memoized source) so consumers that
  // expect a mutable summary list typecheck.
  const submittedResults = useMemo<SubmittedResultSummary[]>(
    () =>
      (submitted?.submittedResults ?? EMPTY_SUBMITTED_RESULTS).map(
        (result) => ({
          programId: result.programId,
          schoolId: result.schoolId,
        }),
      ),
    [submitted?.submittedResults],
  );

  const productClickedCorrelationId = useMemo(
    () => selectedResult?.productClickedCorrelationId || uuid(),
    [selectedResult],
  );

  const { shouldShowThankYou } = useResultsPageConfig();
  const {
    showSlideoutExperience,
    closeSlideoutExperience,
    resetSlideoutExperience,
    dismissSlideoutExperience,
    openSlideoutExperience,
    singleSubmissionExperienceStep,
    goToSlideoutPiiConfirmation,
    goToSlideoutAdditionalQuestions,
    goToSlideoutExitStrategy,
  } = useSingleSubmissionSlideout({ productClickedCorrelationId });

  // Stay enabled for the page lifetime. The Jornaya hook waits until the
  // TCPA disclosure node is in the document before inserting its script.
  const activeProspectToken = useActiveProspectScript(true);
  const jornayaToken = useJornayaScript(true);

  const record = useMemo(() => {
    if (!selectedResult) return undefined;
    return {
      school: {
        id: selectedResult.schoolId,
        slug: selectedResult.schoolSlug,
        name: selectedResult.schoolName,
      },
      program: {
        id: selectedResult.programId,
        leadSource: flowAnswers.leadSource,
      },
    };
  }, [selectedResult, flowAnswers]);

  const shouldUseActiveProspectToken =
    selectedResult?.showActiveProspectScript === true;

  const {
    additionalQuestions,
    checkForAdditionalQuestionsAndSubmit,
    submitWithAdditionalData,
  } = useLeadSubmissionWithQuestions({
    lead,
    needsActiveProspectToken: shouldUseActiveProspectToken,
    activeProspectToken: activeProspectToken || '',
    jornayaToken,
    programId: selectedResult?.programId.toString() || '',
    selectedResult,
    productClickedCorrelationId,
    inputs,
    record,
    applicationCtaURL: selectedResult?.applicationCtaURL || '',
    dslConfigId,
    flowType,
    trafficSource,
    monetizationChannel,
    precreatedConversionId: clickConversionId,
    resolvePrecreatedConversionId: () => clickConversionPromiseRef.current,
    onLeadSubmitted: handleLeadSubmitted,
  });

  // Same-school alternates shown inside the governed duplicate popup, so
  // an intercepted second lead can surface other programs at that school
  // rather than a dead end.
  const { duplicatePrograms, duplicateProgramsLoading } = useDuplicatePrograms({
    duplicateResult: duplicateResult ?? null,
    leadRef,
    degree: flowAnswers.levelOfEducation,
    category: flowAnswers.category,
    subject: flowAnswers.subject,
    qualifiedIds: EMPTY_RESULT_IDS,
    unqualifiedIds: EMPTY_RESULT_IDS,
  });

  const canSubmitLead = useMemo(() => {
    const isSubmittingLead =
      leadSubmissionStatus === SubmissionStatus.SUBMITTING;
    const inputtedNoPii =
      !piiFirstName || !piiLastName || !piiEmail || !piiPhone;
    return !isSubmittingLead && !!selectedResult && !inputtedNoPii;
  }, [
    leadSubmissionStatus,
    selectedResult,
    piiFirstName,
    piiLastName,
    piiEmail,
    piiPhone,
  ]);

  // Record the program that actually submitted. Callers pass the
  // selection captured before the request so a later handoff is not
  // recorded in its place. No-op when no registry is mounted.
  const recordSubmission = useCallback((result?: Result) => {
    if (result) {
      submittedRef.current?.trackSubmission(result);
    }
  }, []);

  // Shared post-submit sequence for both submit paths (regular +
  // additional-questions). `submittedResult` is the selection captured
  // before the request. It is recorded even when the learner opened
  // another program while the request was in flight. Status and
  // navigation run only while that selection is still current, including
  // after the success hold, so a stale callback cannot paint or reset
  // the newer flow. When the thank-you screen is hidden, the drawer is
  // dismissed without a close click and without clearing the success
  // panel, so the contact form does not remount during the exit animation.
  const finishSubmissionAndAdvance = useCallback(
    async (submittedResult: Result | undefined) => {
      recordSubmission(submittedResult);
      if (!submittedResult || selectedResultRef.current !== submittedResult) {
        return;
      }
      setLeadSubmissionStatus(SubmissionStatus.SUBMITTED);
      await new Promise((resolve) =>
        setTimeout(resolve, SUBMITTED_PANEL_HOLD_MS),
      );
      if (selectedResultRef.current !== submittedResult) return;
      if (shouldShowThankYou) {
        setLeadSubmissionStatus(undefined);
        goToSlideoutExitStrategy();
        return;
      }
      dismissSlideoutExperience();
    },
    [
      recordSubmission,
      goToSlideoutExitStrategy,
      dismissSlideoutExperience,
      shouldShowThankYou,
    ],
  );

  // Open the governed surface for a result, bypassing the duplicate-school
  // check. The single low-level seam both the public `openLeadFlow` and
  // the duplicate popup's "view next steps" path drive. `entryStep`
  // selects where the drawer lands: `'pii'` (default) for a fresh
  // lead-capture, or `'exitStrategy'` to drop straight onto the
  // submitted/"what to expect from the advisor" view for a program the
  // user already submitted. `leadSubmissionStatus` stays `undefined` so
  // the exit-strategy STEP renders rather than the full-screen
  // SubmissionStatus panel (which only shows for SUBMITTING/SUBMITTED/…).
  const openGovernedFlow = useCallback(
    (
      result: Result,
      options?: OpenLeadFlowOptions,
      entryStep: 'pii' | 'exitStrategy' = 'pii',
    ) => {
      // A submitted-card reopen and the duplicate popup's "view next steps"
      // ask for the thank-you step. When that screen is off, leave the
      // drawer closed and the inline modal on its cards. Reset rather than
      // close so this skip is not recorded as an X click.
      if (entryStep === 'exitStrategy' && !shouldShowThankYou) {
        resetSlideoutExperience();
        return;
      }
      // Drop detail lifted for a previous selection when the offering changes,
      // so the drawer starts card-only and never flashes stale detail (e.g. the
      // prior card's TCPA/value props) before the new by-id snapshot resolves.
      // The detail/card match gate cannot catch this for a forward-identity
      // `/match` card whose school identity is empty. Scoped to an actual
      // offering change so reopening the same offering keeps its resolved detail.
      if (selectedResultRef.current?.offeringUuid !== result.offeringUuid) {
        setOfferingDetailSource(undefined);
      }
      setSelectedResult(result);
      setLeadSubmissionStatus(undefined);
      setRenderMode(options?.mode ?? 'drawer');
      // Only clear identifiers from a prior submission on a fresh PII
      // capture. An `'exitStrategy'` reopen (already-submitted program,
      // duplicate-popup "view next steps") must keep the identifiers its own
      // submit produced so Apply/extra-CTA interpolation doesn't regress to
      // an empty bag on reopen — mirrors `ResultsPage.selectResult`'s
      // `!hasSubmitted` gate (EDUN-14483).
      if (entryStep === 'pii') {
        setPartnerIdentifiers(undefined);
      }
      // Create the CMS "APP_VIEWED" conversion at this click — the moment the
      // user routes into the governed application view — for a fresh
      // lead-capture (PII entry) on a submittable result. Skipped for the
      // `exitStrategy` reopen of an already-submitted program (its conversion
      // was already created at the original click). Fire-and-forget: the drawer
      // opens immediately; the resolved id is stashed for submit reuse.
      setClickConversionId(undefined);
      if (entryStep === 'pii' && isSubmittableResult(result)) {
        const selectionKey =
          result.productClickedCorrelationId ||
          result.offeringId ||
          result.offeringUuid ||
          String(result.programId);
        const alreadyCreated = createdConversionRef.current.get(selectionKey);
        const inflight = inflightConversionRef.current.get(selectionKey);
        if (alreadyCreated) {
          // Reopen of a card already viewed pre-submit: reuse its conversion
          // rather than mint a duplicate. Synchronous set wins over any
          // superseded in-flight create for a prior open.
          clickConversionPromiseRef.current = Promise.resolve(alreadyCreated);
          setClickConversionId(alreadyCreated);
        } else if (inflight) {
          // Same card opened again before the first create stored an id.
          // Keep that promise so submit completes it and a second
          // conversion-init is not started.
          clickConversionPromiseRef.current = inflight;
        } else {
          const requestToken = (conversionRequestRef.current += 1);
          // Same `flowState.location` merge point the submit path reads (IP
          // inference, overwritten by an answered location question) — carries
          // the visitor's real geo into this click-time create instead of
          // letting CMS fall back to its viewer-country headers for this
          // server-proxied call.
          const locationState = flowState?.['location'] as
            | { countryCode?: string; state?: string }
            | undefined;
          const conversionPromise = ensureConversion(
            result,
            siteConfig,
            result.productClickedCorrelationId || '',
            flowType,
            {
              country: locationState?.countryCode,
              state: locationState?.state,
            },
            trafficSource,
            monetizationChannel,
          ).then((id) => {
            if (
              inflightConversionRef.current.get(selectionKey) ===
              conversionPromise
            ) {
              inflightConversionRef.current.delete(selectionKey);
            }
            if (id) {
              createdConversionRef.current.set(selectionKey, id);
            }
            // Ignore a superseded open's late resolve so a stale id can't leak
            // into a newer selection's state. The promise still resolves to
            // this create's id; a newer open has already replaced the ref.
            if (id && conversionRequestRef.current === requestToken) {
              setClickConversionId(id);
            }
            return id;
          });
          inflightConversionRef.current.set(selectionKey, conversionPromise);
          clickConversionPromiseRef.current = conversionPromise;
        }
      } else {
        clickConversionPromiseRef.current = Promise.resolve(undefined);
      }
      if (entryStep === 'exitStrategy') {
        goToSlideoutExitStrategy();
      } else {
        goToSlideoutPiiConfirmation();
      }
      openSlideoutExperience();
    },
    [
      goToSlideoutPiiConfirmation,
      goToSlideoutExitStrategy,
      openSlideoutExperience,
      resetSlideoutExperience,
      shouldShowThankYou,
      siteConfig,
      flowType,
      trafficSource,
      monetizationChannel,
      flowState,
    ],
  );

  const openLeadFlow = useCallback(
    (result: Result, options?: OpenLeadFlowOptions) => {
      // Re-assert the submittable contract at this platform-owned entry
      // point rather than trusting callers: a numeric `programId` plus
      // either forward (`offeringUuid`) or legacy school-quartet identity.
      // Anything else would slip past the duplicate-school rule below and
      // open / submit an under-specified lead.
      if (!isSubmittableResult(result)) {
        // eslint-disable-next-line no-console
        console.warn(
          '[LeadFlowProvider] openLeadFlow ignored — result must carry a numeric programId and either a non-empty offeringUuid or the full schoolId, schoolSlug, schoolName, and programName quartet.',
        );
        return;
      }
      // Governed duplicate-school rule — owned here in platform
      // runtime, never authorable in DSL.
      const registry = submittedRef.current;
      if (registry) {
        // Re-clicking a card whose OWN program was already submitted
        // (its CTA now reads "Submitted") is not a duplicate attempt —
        // the user wants to revisit what happens next. Land directly on
        // the exit-strategy view ("What to expect from the advisor?")
        // instead of surfacing the duplicate popup.
        if (registry.isProgramSubmitted(result.programId)) {
          openGovernedFlow(result, options, 'exitStrategy');
          return;
        }
        // A second lead attempt for a DIFFERENT program at a school the
        // user has already requested info from is intercepted with the
        // governed `DuplicateSubmissionPopup` instead of opening the
        // flow. The popup's "view next steps" path reopens the governed
        // flow via `openGovernedFlow`, which bypasses this check. Capture
        // the options so the reopen keeps the original render target.
        // Callers that opt out (`skipDuplicateCheck`) bypass the popup.
        if (
          !options?.skipDuplicateCheck &&
          registry.hasSchoolSubmitted(result.schoolId)
        ) {
          setDuplicateResult(result);
          setDuplicateOptions(options);
          return;
        }
      }
      openGovernedFlow(result, options);
    },
    [openGovernedFlow],
  );

  const closeDuplicatePopup = useCallback(() => {
    setDuplicateResult(undefined);
    setDuplicateOptions(undefined);
  }, []);

  const handleDuplicateViewNextSteps = useCallback(() => {
    const dup = duplicateResult;
    const options = duplicateOptions;
    setDuplicateResult(undefined);
    setDuplicateOptions(undefined);
    if (!dup) return;
    // Reopen the governed flow for the program already submitted at this
    // school (the registry stored the full `Result`, so no refetch).
    // Land on the exit-strategy view rather than the PII form, and bypass
    // the duplicate check (the school is already submitted). Replay the
    // captured `options` so the reopen keeps its render target.
    const alreadySubmitted = submittedRef.current?.getSubmittedForSchool(
      dup.schoolId,
    );
    if (alreadySubmitted) {
      openGovernedFlow(alreadySubmitted, options, 'exitStrategy');
    }
  }, [duplicateResult, duplicateOptions, openGovernedFlow]);

  const onProgramSubmit = useCallback(async () => {
    const submittedSelection = selectedResultRef.current;
    try {
      setLeadSubmissionStatus(SubmissionStatus.SUBMITTING);
      const { hasQuestions } = await checkForAdditionalQuestionsAndSubmit();
      if (hasQuestions) {
        if (selectedResultRef.current !== submittedSelection) return;
        setLeadSubmissionStatus(SubmissionStatus.PENDING);
        goToSlideoutAdditionalQuestions();
        return;
      }
      await finishSubmissionAndAdvance(submittedSelection);
    } catch (error) {
      console.error('Error submitting lead:', error);
      if (selectedResultRef.current !== submittedSelection) return;
      setLeadSubmissionStatus(getSubmissionErrorStatus(error));
    }
  }, [
    checkForAdditionalQuestionsAndSubmit,
    goToSlideoutAdditionalQuestions,
    finishSubmissionAndAdvance,
  ]);

  const onAdditionalQuestionsSubmit = useCallback(
    async (formData: Record<string, string | number>) => {
      const submittedSelection = selectedResultRef.current;
      try {
        setLeadSubmissionStatus(SubmissionStatus.SUBMITTING);
        await submitWithAdditionalData(formData);
        await finishSubmissionAndAdvance(submittedSelection);
      } catch (error) {
        console.error('Error submitting lead with additional data:', error);
        if (selectedResultRef.current !== submittedSelection) return;
        setLeadSubmissionStatus(getSubmissionErrorStatus(error));
      }
    },
    [submitWithAdditionalData, finishSubmissionAndAdvance],
  );

  // Mirror `ResultsPageUi.handleSlideoutSubmit`: route the drawer's
  // primary submit between the PRQ form and the regular submit so the
  // platform owns the branch, not the DSL.
  const handleDrawerSubmit = useCallback(
    (formData?: Record<string, string | number>) => {
      if (
        singleSubmissionExperienceStep ===
          SingleSubmissionStep.ADDITIONAL_QUESTIONS &&
        formData
      ) {
        void onAdditionalQuestionsSubmit(formData);
      } else {
        void onProgramSubmit();
      }
    },
    [
      singleSubmissionExperienceStep,
      onAdditionalQuestionsSubmit,
      onProgramSubmit,
    ],
  );

  // Receive the resolved `offeringDetail` source from the observer mounted
  // inside the DSM. Stable identity so the observer's effect deps don't churn.
  const applyOfferingDetail = useCallback(
    (source: ResolvedDataSource | undefined) => {
      setOfferingDetailSource(source);
    },
    [],
  );

  // Non-destructively merge the resolved offering detail-by-id projection
  // onto the open card result for the drawer.
  //
  // The merge only fires when the detail source is `resolved` AND its org
  // identity matches the open selection; otherwise the drawer renders the
  // card-only result:
  //   - `pending` → render card-only fields (detail fills in on resolve). We
  //                 deliberately do NOT raise the `SingleProgramSubmission`
  //                 `isLoading` flag: `isLoading` disables the PII submit
  //                 button, and the detail fetch must never block PII
  //                 capture/submit. The drawer is fully usable while it loads.
  //   - `empty`/`error` → graceful degrade to card-only fields.
  //   - `resolved` + match → merge mapped detail fields.
  //
  // The spread is identity-safe: the mapper only emits drawer-detail keys
  // (never `schoolId`/`programId`/`applicationCtaURL`), and omits empty
  // fields, so card identity/CTA always win.
  const detailMergedResult = useMemo<Result | undefined>(() => {
    if (!selectedResult) return selectedResult;
    if (offeringDetailSource?.status !== 'resolved') return selectedResult;
    try {
      // Robust to either authored `responsePath`: the org object directly
      // (`data.organizations.0`) or the envelope (`data`).
      const orgDetail = resolveOfferingDetailOrg(offeringDetailSource.data);
      if (!offeringDetailMatchesResult(orgDetail, selectedResult)) {
        return selectedResult;
      }
      const detailFields = mapOfferingDetailToResultDetailFields(orgDetail);
      return { ...selectedResult, ...detailFields };
    } catch (error) {
      // The by-id detail projection is real Offerings data, not a fixture:
      // an edge-case shape that slips past the per-field mappers must never
      // blank the drawer as it mounts. Degrade to the card-only result —
      // the same posture the pending / empty / mismatched branches take —
      // so the merge can't be the throw that escapes to the host
      // DynamicExperienceErrorBoundary. Route the swallowed failure through
      // the shared New Relic seam so this silent degradation stays visible
      // in monitoring instead of only reaching the uncollected console.
      newRelicNoticeError(
        error instanceof Error ? error : new Error(String(error)),
        { source: 'LeadFlowProvider', context: 'offering-detail-merge' },
      );
      return selectedResult;
    }
  }, [selectedResult, offeringDetailSource]);

  // The governed submission surface for the selected result. Shared by
  // both render targets: the drawer (below) wraps it in slideout chrome,
  // while inline callers (the popup modal) render this node in place.
  //
  // Renders the detail-merged result so the drawer shows the enriched
  // detail once it resolves; identity/submit fields are unchanged by the
  // merge, so the submission hooks (which read the original
  // `selectedResult`) stay consistent.
  //
  // Wrapped in {@link LeadFlowDrawerBoundary} (SAD §14): the body renders on
  // the render *after* `openLeadFlow` sets state — outside the action
  // dispatcher's try/catch — so a render throw here must degrade to a closed
  // drawer rather than escape to the host `DynamicExperienceErrorBoundary`
  // and blank the whole experience. Both render targets consume this node
  // (the drawer below and inline popup callers), so wrapping it once covers
  // both. Keyed on the selected offering so a fresh selection always mounts a
  // clean boundary (a prior caught error can't stick to a new result), and
  // `onError` resets the slideout so a caught throw leaves no empty chrome —
  // using the silent reset (not the user-close path) so a contained crash is
  // not recorded as a user-initiated drawer close.
  const leadFlowBody = detailMergedResult ? (
    <SingleProgramSubmission
      selectedResult={detailMergedResult}
      step={singleSubmissionExperienceStep}
      startTime={parseInt(flowAnswers?.startDate ?? '', 10)}
      piiLeadErrors={[]}
      setEditMode={setEditMode}
      editMode={editMode}
      setFirstName={setPiiFirstName}
      setLastName={setPiiLastName}
      setEmail={setPiiEmail}
      setPhone={setPiiPhone}
      formErrors={[]}
      setFormErrors={() => {}}
      formHeading='Confirm your contact details:'
      formSubHeading={''}
      onBackButtonClick={goToSlideoutPiiConfirmation}
      onNextSteps={goToSlideoutExitStrategy}
      onProgramSubmit={handleDrawerSubmit}
      firstName={piiFirstName}
      lastName={piiLastName}
      email={piiEmail}
      phone={piiPhone}
      leadSubmissionStatus={leadSubmissionStatus}
      setLeadSubmissionStatus={setLeadSubmissionStatus}
      canSubmit={canSubmitLead}
      isLoading={false}
      questions={additionalQuestions}
      productClickedCorrelationId={productClickedCorrelationId}
      onCloseExperience={closeSlideoutExperience}
      dslConfigId={dslConfigId}
      studentLoansInlineContent={studentLoansInlineContent}
      extraCtaConfig={extraCtaConfig}
      // Governed (DSL) surfaces read the same generic identifier bag as the
      // imperative ResultsPage path so HEN's Apply/extra-CTA field maps
      // interpolate real values instead of an empty bag (EDUN-14483).
      partnerIdentifiers={partnerIdentifiers}
      // Only the `'inline'` render target (the DSL popup / Recommender
      // modal) needs an explicit exit-strategy close "x" — the
      // `'drawer'` target already offers its own close control.
      showExitStrategyCloseButton={renderMode === 'inline'}
    />
  ) : null;

  const leadFlowContent = leadFlowBody ? (
    // `||` (not `??`) so an empty-string `offeringUuid` — which legacy results
    // carry and `isSubmittableResult` treats as absent — falls back to
    // `programId`. Otherwise every such result would share an empty-string key
    // and the boundary's caught-error state could leak across selections.
    <LeadFlowDrawerBoundary
      key={selectedResult?.offeringUuid || selectedResult?.programId}
      onError={resetSlideoutExperience}
    >
      {leadFlowBody}
    </LeadFlowDrawerBoundary>
  ) : null;

  const value = useMemo<LeadFlowContextValue>(
    () => ({
      openLeadFlow,
      closeLeadFlow: closeSlideoutExperience,
      // Expose the detail-merged result so consumers (and tests) observe
      // the enriched drawer detail; identity/CTA fields are unchanged.
      selectedResult: detailMergedResult,
      isOpen: showSlideoutExperience,
      mode: renderMode,
      firstName:
        piiFirstName || flowInputs.firstName?.value?.toString() || undefined,
      leadFlowContent,
      submittedResults,
      applyOfferingDetail,
    }),
    [
      openLeadFlow,
      closeSlideoutExperience,
      detailMergedResult,
      showSlideoutExperience,
      renderMode,
      piiFirstName,
      flowInputs.firstName?.value,
      leadFlowContent,
      submittedResults,
      applyOfferingDetail,
    ],
  );

  return (
    <LeadFlowContext.Provider value={value}>
      {children}
      {/* In `'inline'` mode the popup modal renders `leadFlowContent`
          itself, so the provider mounts no drawer chrome. */}
      {renderMode === 'drawer' && (
        <SingleSubmissionDrawer
          open={showSlideoutExperience}
          onClose={closeSlideoutExperience}
        >
          {leadFlowContent}
        </SingleSubmissionDrawer>
      )}
      {/* Governed duplicate-school surface. Renders only when the
          duplicate rule intercepts a second lead to an already-submitted
          school. Same-school alternates come from `useDuplicatePrograms`
          so the popup can offer other programs at that school. */}
      {duplicateResult && (
        <DuplicateSubmissionPopup
          result={duplicateResult}
          otherPrograms={duplicatePrograms}
          isLoading={duplicateProgramsLoading}
          onClose={closeDuplicatePopup}
          onViewNextSteps={handleDuplicateViewNextSteps}
        />
      )}
    </LeadFlowContext.Provider>
  );
};

/**
 * Read the governed lead-flow entry point. Falls back to a safe no-op
 * (single warn) outside a {@link LeadFlowProvider}.
 */
export function useLeadFlow(): LeadFlowContextValue {
  return useContext(LeadFlowContext) ?? NOOP_LEAD_FLOW;
}

/**
 * Optional variant used by the bridge to detect host ownership without
 * triggering the no-op warning.
 */
export function useLeadFlowOptional(): LeadFlowContextValue | undefined {
  return useContext(LeadFlowContext);
}

export default LeadFlowProvider;
